/**
 * @module    public/js/modals/callScriptModal.js
 * @function  openCallScriptModal
 * @layer     ui-modal
 * @summary   Opens a floating read-only call-script panel for Focus Mode call steps.
 *
 * @description
 * Renders one or more configured call scripts, resolves merge variables through
 * TemplateService, and lets the seller switch between script variants such as
 * answered, voicemail, or gatekeeper without affecting task completion.
 *
 * @author    TopSail AI
 * @version   1.0.0
 * @since     2026-03-27
 */

import { applyFocusModeTooltips } from '../focusmode/tooltipService.js';
import { TemplateUpdateFlow } from './templateUpdateFlow.js';

const FONT_STACKS = new Map([
  ['aptos', '"Aptos", "Calibri", "Segoe UI", sans-serif'],
  ['calibri', '"Calibri", "Segoe UI", sans-serif'],
  ['arial', 'Arial, "Helvetica Neue", Helvetica, sans-serif'],
  ['times-new-roman', '"Times New Roman", Times, serif'],
  ['verdana', 'Verdana, Geneva, sans-serif'],
  ['tahoma', 'Tahoma, Geneva, sans-serif'],
  ['georgia', 'Georgia, Times, "Times New Roman", serif'],
  ['trebuchet', '"Trebuchet MS", Arial, sans-serif'],
]);

/**
 * Escape plain text for safe insertion into HTML strings.
 *
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return `${value ?? ''}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalize configured call scripts into complete label/template pairs.
 *
 * @param {Array<{ label?: string, templateId?: string, template?: string }>|null|undefined} callScripts
 * @returns {Array<{ label: string, templateId: string }>}
 */
function normaliseCallScripts(callScripts) {
  const entries = Array.isArray(callScripts) ? callScripts : [];
  return entries.reduce((acc, entry) => {
    const label = String(entry?.label ?? '').trim();
    const templateId = String(
      entry?.templateId ?? entry?.template ?? ''
    ).trim();
    if (!label || !templateId) return acc;
    acc.push({ label, templateId });
    return acc;
  }, []);
}

/**
 * Resolve modal and template services from explicit args or bootstrap DI.
 *
 * @param {object} params
 * @param {object|null|undefined} params.di
 * @param {object|null|undefined} params.modalService
 * @param {object|null|undefined} params.templateService
 * @param {object|null|undefined} params.errorUI
 * @returns {Promise<{ di: object|null, modalService: object, templateService: object, errorUI: object|null }>}
 */
async function resolveDependencies({
  di,
  modalService,
  templateService,
  errorUI,
}) {
  if (modalService && templateService) {
    return {
      di: di || {
        modalService,
        templateService,
        errorUI: errorUI || null,
      },
      modalService,
      templateService,
      errorUI: errorUI || di?.errorUI || null,
    };
  }

  let resolvedDi = di || null;
  try {
    resolvedDi =
      resolvedDi || (await import('../bootstrap/focusmode-app.js')).default;
  } catch (err) {
    console.debug('[CallScriptModal] focusmode-app DI unavailable', err);
  }

  if (!resolvedDi?.modalService || !resolvedDi?.templateService) {
    try {
      resolvedDi = resolvedDi || (await import('../bootstrap/app.js')).default;
      if (!resolvedDi?.modalService || !resolvedDi?.templateService) {
        resolvedDi = (await import('../bootstrap/app.js')).default;
      }
    } catch (err) {
      console.debug('[CallScriptModal] app DI unavailable', err);
    }
  }

  return {
    di: resolvedDi,
    modalService: modalService || resolvedDi?.modalService || resolvedDi?.modal,
    templateService: templateService || resolvedDi?.templateService,
    errorUI: errorUI || resolvedDi?.errorUI || null,
  };
}

/**
 * Ensure the modal host exists and is registered with the shared modal service.
 *
 * @param {object} modalService
 * @returns {HTMLElement}
 */
function ensureHost(modalService) {
  let host = document.getElementById('callScriptModal');
  if (!host) {
    host = document.createElement('div');
    host.id = 'callScriptModal';
    host.className = 'hidden call-script-modal-layer';
    document.body.appendChild(host);
    modalService.register('callScriptModal', host);
  }
  host.classList.remove('modal-overlay');
  host.classList.add('call-script-modal-layer');
  host.removeAttribute('role');
  return host;
}

/**
 * Open a read-only modal that renders configured call scripts.
 *
 * @param {object} [options={}]
 * @param {string} [options.title='Call Scripts']
 * @param {Array<{ label?: string, templateId?: string, template?: string }>} [options.callScripts=[]]
 * @param {Record<string, unknown>} [options.templateData={}]
 * @param {Record<string, unknown>} [options.mergeVars={}]
 * @param {object|null} [options.di]
 * @param {object|null} [options.modalService]
 * @param {object|null} [options.templateService]
 * @param {object|null} [options.errorUI]
 * @param {boolean} [options.enableTooltips=true]
 * @returns {Promise<void>}
 */
export async function openCallScriptModal(options = {}) {
  const {
    title = 'Call Scripts',
    callScripts = [],
    templateData = {},
    mergeVars = {},
    enableTooltips = true,
  } = options;
  const normalisedScripts = normaliseCallScripts(callScripts);
  if (!normalisedScripts.length) return;

  const { di, modalService, templateService, errorUI } =
    await resolveDependencies(options);
  if (!modalService || !templateService?.render) {
    throw new Error('CallScriptModal dependencies unavailable');
  }

  const templateUpdateFlow = new TemplateUpdateFlow({
    di: di || {
      templateService,
      errorUI,
    },
    modalService,
    templateService,
    templateData,
    mergeVars,
    includeSubject: false,
    hostModalId: 'callScriptModal',
    confirmModalId: 'callScriptTemplateUpdateConfirmModal',
    updateImpactLabel: 'future call scripts for this step',
  });
  const host = ensureHost(modalService);
  host.innerHTML = /* html */ `
    <div
      class="modal-container call-script-modal"
      role="dialog"
      aria-modal="false"
      aria-labelledby="callScriptModalTitle"
      tabindex="-1"
    >
      <header class="call-script-modal__header focus-modal__header drag-handle" data-role="drag-handle">
        <div class="focus-modal__title-group">
          <h3 id="callScriptModalTitle" class="call-script-modal__title">${escapeHtml(title)}</h3>
          <p class="call-script-modal__subtitle">
            Review the configured script for the live call situation. Drag or resize this panel while you work in Notes.
          </p>
        </div>
        <div class="call-script-modal__header-actions focus-modal__header-actions">
          <button
            type="button"
            id="call-script-edit-template"
            class="btn-pill btn--grey btn--info-sm btn--secondary-action"
          >
            Edit Template
          </button>
          <button
            type="button"
            id="call-script-update-template"
            class="btn-pill btn--orange btn--info-sm btn--primary-action hidden"
          >
            Update Template
          </button>
        </div>
      </header>

      <div class="call-script-modal__body">
        <nav class="call-script-modal__nav" aria-label="Call script variants">
          ${normalisedScripts
            .map(
              ({ label, templateId }, index) => /* html */ `
                <button
                  type="button"
                  class="call-script-modal__tab${index === 0 ? ' is-active' : ''}"
                  data-action="select"
                  data-index="${index}"
                  data-focus-tooltip="Open the ${escapeHtml(label)} script."
                >
                  <span class="call-script-modal__tab-label">${escapeHtml(label)}</span>
                  <span class="call-script-modal__tab-meta">${escapeHtml(templateId)}</span>
                </button>`
            )
            .join('')}
        </nav>

        <section class="call-script-modal__panel" aria-live="polite">
          <div class="call-script-modal__status" data-role="status">
            Loading script…
          </div>
          <div class="call-script-modal__preview" data-role="preview"></div>
          <div class="call-script-modal__editor hidden" data-role="editor-shell">
            <div data-role="editor"></div>
          </div>
        </section>
      </div>

      <footer class="call-script-modal__footer focus-modal__footer">
        <p
          class="call-script-modal__template-note focus-modal__footer-note hidden"
          data-role="template-note"
        ></p>
        <div
          class="call-script-modal__footer-actions focus-modal__footer-actions"
          data-actions="1"
        >
          <button
            type="button"
            class="btn-pill btn--grey btn--info-sm btn--secondary-action"
            data-action="close"
          >
            Close
          </button>
        </div>
      </footer>
    </div>`;

  applyFocusModeTooltips(host, { enableTooltips });

  return new Promise((resolve) => {
    const modalEl = host.querySelector('.call-script-modal');
    const dragHandleEl = host.querySelector('[data-role="drag-handle"]');
    const statusEl = host.querySelector('[data-role="status"]');
    const previewEl = host.querySelector('[data-role="preview"]');
    const editorShellEl = host.querySelector('[data-role="editor-shell"]');
    const editorMountEl = host.querySelector('[data-role="editor"]');
    const templateNoteEl = host.querySelector('[data-role="template-note"]');
    const editTemplateBtn = host.querySelector('#call-script-edit-template');
    const updateTemplateBtn = host.querySelector(
      '#call-script-update-template'
    );
    const tabButtons = Array.from(
      host.querySelectorAll('button[data-action="select"]')
    );
    const closeButtons = Array.from(
      host.querySelectorAll('button[data-action="close"]')
    );
    const ctx = { ...(mergeVars || {}), ...(templateData || {}) };
    const toolbarOptions = [
      [{ size: [] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
    ];
    let activeIndex = 0;
    let dragging = false;
    let dragOffsetX = 0;
    let dragOffsetY = 0;
    let quill = null;
    let suppressQuillSync = false;
    let updateEnabled = false;
    let templateEditMode = false;
    let currentRawHtml = '';
    let currentRenderedHtml = '';
    let currentFontFamily = '';

    /**
     * Show a lightweight inline status note in the footer area.
     *
     * @param {string} message
     * @param {'info'|'warning'|'success'} [tone='info']
     * @returns {void}
     */
    function setTemplateNote(message, tone = 'info') {
      if (!templateNoteEl) return;
      const nextMessage = String(message ?? '').trim();
      templateNoteEl.textContent = nextMessage;
      templateNoteEl.classList.toggle('hidden', !nextMessage);
      templateNoteEl.dataset.tone = nextMessage ? tone : '';
    }

    /**
     * Apply the currently rendered script into the preview surface.
     *
     * @param {string} html
     * @param {string} fontFamily
     * @returns {void}
     */
    function applyPreviewState(html, fontFamily = '') {
      currentRenderedHtml = html || '';
      currentFontFamily = fontFamily || '';
      previewEl.innerHTML =
        currentRenderedHtml ||
        '<p class="call-script-modal__empty">This script is empty.</p>';
      if (currentFontFamily) {
        previewEl.style.fontFamily = currentFontFamily;
      } else {
        previewEl.style.removeProperty('font-family');
      }
    }

    /**
     * Synchronize button, helper, and tab states for the current panel mode.
     *
     * @returns {void}
     */
    function syncTemplateControls() {
      if (editTemplateBtn) {
        editTemplateBtn.disabled = !updateEnabled;
        editTemplateBtn.textContent = templateEditMode
          ? 'Cancel Update'
          : 'Edit Template';
      }
      if (updateTemplateBtn) {
        updateTemplateBtn.classList.toggle('hidden', !templateEditMode);
        updateTemplateBtn.disabled = !updateEnabled || !templateEditMode;
      }
      tabButtons.forEach((btn) => {
        btn.disabled = templateEditMode;
        btn.setAttribute('aria-disabled', templateEditMode ? 'true' : 'false');
      });

      if (!updateEnabled) {
        setTemplateNote('Template updates are unavailable for this script.');
        return;
      }
      if (templateEditMode) {
        setTemplateNote(
          'Editing template source. Update to save changes or cancel to discard.',
          'warning'
        );
        return;
      }
      setTemplateNote('');
    }

    /**
     * Persist the panel frame so repeated openings feel stable.
     *
     * @returns {void}
     */
    function persistPanelFrame() {
      if (!modalEl) return;
      host.dataset.panelLeft = modalEl.style.left || '';
      host.dataset.panelTop = modalEl.style.top || '';
      host.dataset.panelWidth = modalEl.style.width || '';
      host.dataset.panelHeight = modalEl.style.height || '';
    }

    /**
     * Apply the initial size and position for the floating panel.
     *
     * @returns {void}
     */
    function applyInitialPanelFrame() {
      if (!modalEl) return;
      const margin = 12;
      const navOffset = Math.max(
        (document.querySelector('nav')?.offsetHeight ?? 0) + 8,
        margin
      );
      const defaultWidth = Math.max(
        420,
        Math.min(640, window.innerWidth - margin * 2)
      );
      const defaultHeight = Math.max(
        360,
        Math.min(620, window.innerHeight - navOffset - 24)
      );
      const defaultLeft = Math.max(
        margin,
        Math.min(
          window.innerWidth * 0.08,
          window.innerWidth - defaultWidth - margin
        )
      );

      modalEl.style.width =
        host.dataset.panelWidth || `${Math.round(defaultWidth)}px`;
      modalEl.style.height =
        host.dataset.panelHeight || `${Math.round(defaultHeight)}px`;
      modalEl.style.left =
        host.dataset.panelLeft || `${Math.round(defaultLeft)}px`;
      modalEl.style.top = host.dataset.panelTop || `${Math.round(navOffset)}px`;
    }

    /**
     * Lazily hydrate Quill for the in-panel template update experience.
     *
     * @returns {Promise<unknown>}
     */
    async function ensureEditor() {
      if (quill || !editorMountEl) return quill;

      if (!window.Quill) {
        await import(
          'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.min.js'
        );
        if (!document.getElementById('quill-css')) {
          const link = document.createElement('link');
          link.id = 'quill-css';
          link.rel = 'stylesheet';
          link.href =
            'https://cdn.jsdelivr.net/npm/quill@1.3.7/dist/quill.snow.css';
          document.head.appendChild(link);
        }
      }

      quill = new window.Quill(editorMountEl, {
        theme: 'snow',
        modules: {
          toolbar: toolbarOptions,
        },
      });
      quill.root.setAttribute('aria-label', 'Call script template editor');
      quill.on('text-change', () => {
        if (suppressQuillSync || !templateEditMode) {
          return;
        }
        currentRawHtml = quill.root.innerHTML;
      });
      return quill;
    }

    /**
     * Update the Quill editor contents without re-triggering sync handlers.
     *
     * @param {string} html
     * @returns {void}
     */
    function setEditorHtml(html) {
      if (!quill) return;
      suppressQuillSync = true;
      quill.root.innerHTML = html || '';
      suppressQuillSync = false;
    }

    /**
     * Begin dragging the floating panel from its header.
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    function handleDragStart(event) {
      if (!modalEl || window.innerWidth <= 900 || event.button !== 0) return;
      if (
        event.target instanceof Element &&
        event.target.closest('button, a, input, select, textarea')
      ) {
        return;
      }
      const rect = modalEl.getBoundingClientRect();
      dragging = true;
      dragOffsetX = event.clientX - rect.left;
      dragOffsetY = event.clientY - rect.top;
      modalEl.classList.add('is-dragging');
      event.preventDefault();
    }

    /**
     * Move the floating panel while the pointer is dragging the header.
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    function handleDragMove(event) {
      if (!dragging || !modalEl) return;
      modalEl.style.left = `${Math.round(event.clientX - dragOffsetX)}px`;
      modalEl.style.top = `${Math.round(event.clientY - dragOffsetY)}px`;
    }

    /**
     * Finish a drag or resize interaction and store the latest frame.
     *
     * @returns {void}
     */
    function handlePointerRelease() {
      if (!modalEl) return;
      dragging = false;
      modalEl.classList.remove('is-dragging');
      persistPanelFrame();
    }

    /**
     * Re-clamp the panel after viewport changes.
     *
     * @returns {void}
     */
    function handleViewportResize() {
      persistPanelFrame();
    }

    /**
     * Toggle edit mode for the currently active script template.
     *
     * @param {boolean} nextMode
     * @returns {Promise<void>}
     */
    async function setTemplateEditMode(nextMode) {
      if (nextMode && !updateEnabled) {
        return;
      }

      templateEditMode = nextMode;
      templateUpdateFlow.isEditing = nextMode;
      if (templateEditMode) {
        templateUpdateFlow.enterEditMode();
        await ensureEditor();
        currentRawHtml =
          templateUpdateFlow.getRawTemplateHtml() ||
          currentRawHtml ||
          currentRenderedHtml;
        setEditorHtml(currentRawHtml);
        previewEl.classList.add('hidden');
        editorShellEl?.classList.remove('hidden');
        quill?.enable?.(true);
        quill?.root?.focus?.();
      } else {
        currentRawHtml =
          quill?.root?.innerHTML ??
          templateUpdateFlow.getRawTemplateHtml() ??
          currentRawHtml;
        templateUpdateFlow.exitEditMode({ html: currentRawHtml });
        editorShellEl?.classList.add('hidden');
        previewEl.classList.remove('hidden');
        applyPreviewState(currentRenderedHtml, currentFontFamily);
      }

      const current = normalisedScripts[activeIndex];
      if (current) {
        statusEl.textContent = templateEditMode
          ? `Editing ${current.label}`
          : current.label;
      }
      syncTemplateControls();
    }

    /**
     * Close the modal and resolve the caller promise.
     *
     * @returns {void}
     */
    function close() {
      closeButtons.forEach((btn) => btn.removeEventListener('click', close));
      tabButtons.forEach((btn) =>
        btn.removeEventListener('click', handleSelectClick)
      );
      editTemplateBtn?.removeEventListener('click', handleEditTemplate);
      updateTemplateBtn?.removeEventListener('click', handleUpdateTemplate);
      dragHandleEl?.removeEventListener('mousedown', handleDragStart);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handlePointerRelease);
      window.removeEventListener('resize', handleViewportResize);
      handlePointerRelease();
      modalService.close('callScriptModal');
      resolve();
    }

    /**
     * Load and render the selected call script into the preview panel.
     *
     * @param {number} index
     * @returns {Promise<void>}
     */
    async function renderScript(index) {
      const nextIndex = Number(index);
      if (Number.isNaN(nextIndex) || !normalisedScripts[nextIndex]) return;

      activeIndex = nextIndex;
      tabButtons.forEach((btn, buttonIndex) => {
        const isActive = buttonIndex === activeIndex;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      const current = normalisedScripts[activeIndex];
      updateEnabled = false;
      currentRawHtml = '';
      currentRenderedHtml = '';
      currentFontFamily = '';
      syncTemplateControls();
      statusEl.textContent = `Loading ${current.label}…`;
      applyPreviewState('', '');

      try {
        const updateState = await templateUpdateFlow.loadRawTemplate(
          current.templateId
        );
        updateEnabled = !!updateState?.updateEnabled;
        currentRawHtml = templateUpdateFlow.getRawTemplateHtml() || '';
        const rendered = await templateService.render(current.templateId, ctx);
        const fontFamily =
          FONT_STACKS.get(rendered?.fontFamily) || rendered?.fontFamily || '';
        statusEl.textContent = current.label;
        applyPreviewState(rendered?.html || '', fontFamily);
        syncTemplateControls();
      } catch (err) {
        console.error('[CallScriptModal] render failed', err);
        statusEl.textContent = current.label;
        applyPreviewState(
          '<p class="call-script-modal__error">Unable to load this script right now.</p>',
          ''
        );
        syncTemplateControls();
      }
    }

    /**
     * Handle clicks on a script selector tab.
     *
     * @param {MouseEvent} event
     * @returns {Promise<void>}
     */
    async function handleSelectClick(event) {
      if (templateEditMode) {
        errorUI?.global?.({
          text: 'Finish updating or cancel first before switching scripts.',
          type: 'info',
          duration: 3000,
        });
        return;
      }
      const nextIndex = Number(event.currentTarget?.dataset?.index);
      await renderScript(nextIndex);
    }

    /**
     * Toggle edit mode for the active call script template.
     *
     * @returns {Promise<void>}
     */
    async function handleEditTemplate() {
      if (!updateEnabled) {
        errorUI?.global?.({
          text: 'Template updates are unavailable for this script.',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
      await setTemplateEditMode(!templateEditMode);
    }

    /**
     * Persist the edited script source and refresh the rendered preview.
     *
     * @returns {Promise<void>}
     */
    async function handleUpdateTemplate() {
      const current = normalisedScripts[activeIndex];
      if (!current?.templateId) return;
      if (!updateEnabled) {
        errorUI?.global?.({
          text: 'Template updates are unavailable for this script.',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
      if (!templateEditMode) {
        errorUI?.global?.({
          text: 'Switch to Edit Template to update the saved script.',
          type: 'info',
          duration: 3000,
        });
        return;
      }

      if (updateTemplateBtn) {
        updateTemplateBtn.disabled = true;
        updateTemplateBtn.textContent = 'Updating…';
      }

      const rawHtml =
        quill?.root?.innerHTML ??
        templateUpdateFlow.getRawTemplateHtml() ??
        currentRawHtml;

      try {
        const result = await templateUpdateFlow.confirmAndUpdate({
          html: rawHtml,
          templateId: current.templateId,
        });
        if (host.classList.contains('hidden')) {
          modalService.open('callScriptModal');
        }
        if (!result || result.updated !== true) {
          if (result?.reason === 'missing-tenant-id') {
            errorUI?.global?.({
              text: 'Cannot update template because tenant information is missing.',
              type: 'error',
              duration: 4000,
            });
          }
          return;
        }

        currentRawHtml = rawHtml;
        if (templateService?.renderFromSource) {
          const rendered = await templateService.renderFromSource(
            { html: rawHtml },
            ctx
          );
          const fontFamily =
            FONT_STACKS.get(rendered?.fontFamily) || rendered?.fontFamily || '';
          applyPreviewState(rendered?.html || '', fontFamily);
        } else {
          await renderScript(activeIndex);
        }
        await setTemplateEditMode(false);
        statusEl.textContent = current.label;
        errorUI?.global?.({
          text: 'Template updated.',
          type: 'success',
          duration: 3000,
        });
      } catch (err) {
        console.error('[CallScriptModal] template update failed', err);
        errorUI?.global?.({
          text: 'Failed to update template. Check permissions and try again.',
          type: 'error',
          duration: 4000,
        });
      } finally {
        if (updateTemplateBtn) {
          updateTemplateBtn.disabled = false;
          updateTemplateBtn.textContent = 'Update Template';
        }
      }
    }

    closeButtons.forEach((btn) => btn.addEventListener('click', close));
    tabButtons.forEach((btn) =>
      btn.addEventListener('click', handleSelectClick)
    );
    editTemplateBtn?.addEventListener('click', handleEditTemplate);
    updateTemplateBtn?.addEventListener('click', handleUpdateTemplate);
    dragHandleEl?.addEventListener('mousedown', handleDragStart);
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handlePointerRelease);
    window.addEventListener('resize', handleViewportResize);

    applyInitialPanelFrame();
    modalService.open('callScriptModal');
    modalEl?.focus?.();
    void renderScript(activeIndex);
  });
}
